import type { Executable, LanguageAdapter } from '../../core/types';
import { JSRuntimeGlue } from './shim';
import { javascript } from '@codemirror/lang-javascript';
import { lintGutter } from '@codemirror/lint';
import { customScrollbar } from '../../editor/themeScrollbar';
import { breakpointGutter } from '../../editor/breakpoints';
import { acornLinterExtension } from '../../editor/acornLinterExtension';

let _getBreakpoints: () => number[] = () => [];

// ---------- Prettier lazy loader (via <script> injecté) ----------
let _prettier: any | null = null;
let _prettierBabel: any | null = null;
let _prettierEstree: any | null = null;

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        // évite doublons
        if (document.querySelector(`script[data-prettier-src="${src}"]`)) {
            resolve(); return;
        }
        const s = document.createElement('script');
        s.async = true;
        s.defer = true;
        s.src = src;
        s.dataset.prettierSrc = src;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
    });
}

async function ensurePrettierLoaded() {
    if (_prettier && _prettierBabel) return;

    const w = window as any;

    // 1) Déjà présent en global ?
    if (w.prettier && (w.prettierPlugins?.babel || w.prettierPlugins?.estree)) {
        _prettier = w.prettier;
        _prettierBabel = w.prettierPlugins?.babel;
        _prettierEstree = w.prettierPlugins?.estree;
        return;
    }

    // 2) Injecte les scripts UMD depuis unpkg
    try {
        await loadScript('https://unpkg.com/prettier@3.3.3/standalone.js');
        await Promise.all([
            loadScript('https://unpkg.com/prettier@3.3.3/plugins/babel.js'),
            loadScript('https://unpkg.com/prettier@3.3.3/plugins/estree.js'),
        ]);
        _prettier = w.prettier;
        _prettierBabel = w.prettierPlugins?.babel;
        _prettierEstree = w.prettierPlugins?.estree;
    } catch (e) {
        console.warn('[JS-IDE] Impossible de charger Prettier depuis le CDN.', e);
    }
}

export const JavascriptAdapter: LanguageAdapter & {
    /** Permet à l’IDE d’activer le bouton Format si présent */
    format?: (src: string) => Promise<string>;
} = {
    id: 'javascript',

    // Extensions CodeMirror
    editorExtensions() {
        return [
            javascript({ jsx: true }),
            lintGutter(),
            customScrollbar,
            breakpointGutter(() => _getBreakpoints()),
            acornLinterExtension,
        ];
    },

    async compile(code, bridge, controls) {
        _getBreakpoints = () => controls.breakpoints.getAll().map(b => b.line);
        const payload = await JSRuntimeGlue.compile(code, bridge, controls);
        const exec: Executable = { lang: 'javascript', payload };
        return exec;
    },

    async runStep(exec, controls) { await JSRuntimeGlue.runStep(exec.payload, controls); },

    async runAll(exec) {
        const { interpreter, executionContext } = exec.payload;
        executionContext.isRunning = true;
        executionContext.isPaused = false;
        void interpreter.runAll(); // exécution persistante
    },

    async stop(exec) {
        const { interpreter, executionContext } = exec.payload;
        try {
            executionContext.isRunning = false;
            executionContext.isPaused = false;
            exec.payload.dispose?.();   // nettoie timers/listeners
            interpreter?.stop?.();      // coupe la boucle
        } finally {
            (window as any).__IDE_BRIDGE__?.events?.emit?.('ide:stop');
        }
    },

    registerEventSyntax(_code: string) { return {}; },

    // ---------- Formatage JavaScript avec Prettier ----------
    async format(src: string): Promise<string> {
        await ensurePrettierLoaded();
        if (!_prettier || !_prettierBabel) return src; // pas dispo → on garde tel quel
        try {
            const plugins = _prettierEstree ? [_prettierBabel, _prettierEstree] : [_prettierBabel];
            return _prettier.format(src, {
                parser: 'babel',
                plugins,
                semi: true,
                singleQuote: true,
                trailingComma: 'es5',
                tabWidth: 2,
                bracketSpacing: true,
                arrowParens: 'always',
            });
        } catch (e) {
            console.warn('[JS-IDE] Prettier a échoué, code conservé.', e);
            return src;
        }
    },
};
